// ═══════════════════════════════════════════════════════════════════════════════
// Discovery Interview Log — customer-discovery interviews for the 30-day sprint
//
// Add / edit / delete interviews, view them in a table, and export to CSV
// for the weekly pattern analysis. Backed by the discovery_interviews table
// (super-admin only via RLS).
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  ClipboardList, Plus, Pencil, Trash2, Download, Upload, FileSpreadsheet,
  ListChecks, ChevronDown, ChevronRight, Sparkles, Copy,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import ReactMarkdown from 'react-markdown';
import { Modal, Spinner } from '@/components/shared/UIComponents';
import {
  fetchDiscoveryInterviews,
  createDiscoveryInterview,
  createDiscoveryInterviews,
  updateDiscoveryInterview,
  deleteDiscoveryInterview,
  analyzeDiscoveryInterviews,
  fetchSurveyViewCount,
} from '@/lib/api';
import { cn } from '@/utils/helpers';
import type {
  DiscoveryInterview,
  InterviewSource,
  InterviewPersona,
  InterviewWedge,
  CreateDiscoveryInterviewInput,
} from '@/types';

// ── Option config ──────────────────────────────────────────────────────────

const SOURCE_OPTIONS: { value: InterviewSource; label: string; cls: string }[] = [
  { value: 'stranger', label: 'Stranger', cls: 'badge-emerald' },
  { value: 'network',  label: 'Network',  cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400' },
];

const PERSONA_OPTIONS: { value: InterviewPersona; label: string }[] = [
  { value: 'traveler', label: 'Traveler' },
  { value: 'creator',  label: 'Creator' },
];

const WEDGE_OPTIONS: { value: InterviewWedge; label: string; cls: string }[] = [
  { value: 'companion', label: 'Companion', cls: 'bg-teal-100 text-teal-700 dark:bg-teal-900/20 dark:text-teal-400' },
  { value: 'group',     label: 'Group',     cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400' },
  { value: 'creator',   label: 'Creator',   cls: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-400' },
  { value: 'none',      label: 'None',      cls: 'bg-surface-100 text-surface-500' },
];

// ── Week 1 interview questionnaire (The Mom Test) ────────────────────────────

const QUESTIONNAIRE: { title: string; start: number; qs: string[] }[] = [
  { title: 'Warm-up · get them telling a real story', start: 1, qs: [
    'Tell me about the last trip you took — where, how long, who with?',
    'Walk me through one day of it — what did you actually do, roughly hour by hour?',
  ] },
  { title: 'The in-trip moment · companion wedge', start: 3, qs: [
    'What did you plan ahead vs. figure out on the spot?',
    'Was there a moment you had unexpected free time or a gap in the day — what did you do?',
    'Ever stood somewhere unsure what to do or where to go next? Tell me about that time.',
    'When you wanted to find something good nearby in the moment, how did you decide?',
    'Anything you only found out later that you’d missed — and wished you’d known while you were there?',
  ] },
  { title: 'Deciding together · group wedge', start: 8, qs: [
    'Think about your last trip with other people — how did you all decide what to do each day?',
    'Where did that planning happen — group chat, a doc, in person? What got messy or lost?',
    'Someone usually becomes the “organiser.” Was that you? What did it cost you in time or stress?',
  ] },
  { title: 'Content & itineraries · Blueprints wedge', start: 11, qs: [
    'Ever followed someone’s trip online and thought “I want to do exactly that”? What did you actually do next?',
    'Have you ever tried to get someone’s itinerary — DM’d them, bought a guide, copied a blog? How did that go?',
  ] },
  { title: 'Behaviour & spend · is the pain real?', start: 13, qs: [
    'Which apps/tools did you use during the trip? Which did you open most?',
    'What about the trip stressed you out or ate time you hadn’t expected?',
    'Have you ever paid for anything to help with a trip — a guide, app, planner, tour, agent? What, and why?',
    'Ever had a plan trapped in a PDF, email or doc you wished was just live in an app? What did you do with it?',
    'If you could wave a wand and fix one annoying thing about how you travel, what would it be?',
  ] },
  { title: 'Close', start: 18, qs: [
    'Who else do you know who travels like you that I should talk to?',
  ] },
];

const QUESTIONNAIRE_RULES = 'Talk about their life, not your idea. Ask about specific things that already happened, never opinions about the future. Listen 80% of the time. Never pitch.';
const QUESTIONNAIRE_AVOID = '“Would you use an app that…?” · “Do you think this is a good idea?” · “How much would you pay for…?” — hypotheticals produce polite lies. Ask what they did, not what they would.';

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function formatDate(iso: string): string {
  // interview_date is YYYY-MM-DD; render without timezone shift
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function sourceStyle(s: string) {
  return SOURCE_OPTIONS.find(o => o.value === s) || SOURCE_OPTIONS[0];
}
function wedgeStyle(w: string | null) {
  return WEDGE_OPTIONS.find(o => o.value === w) || null;
}

// ── CSV export ───────────────────────────────────────────────────────────────

function toCsv(rows: DiscoveryInterview[]): string {
  const headers = [
    'Date', 'Name', 'Source', 'Persona', 'Age group', 'Ethnicity', 'Channel', 'Last trip',
    'Pain', 'Wedge', 'Tools', 'Paid for?', 'Referral', 'Takeaway', 'Notes', 'Logged at',
  ];
  const esc = (v: unknown) => {
    const s = v == null ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = rows.map(r => [
    r.interview_date, r.name, r.source, r.persona, r.age_group, r.ethnicity, r.channel, r.last_trip,
    r.pain, r.wedge, r.tools, r.paid, r.referral, r.takeaway, r.notes, r.created_at,
  ].map(esc).join(','));
  return [headers.join(','), ...lines].join('\n');
}

function downloadCsv(rows: DiscoveryInterview[]) {
  const blob = new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `discovery-interviews-${today()}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function downloadXlsx(rows: DiscoveryInterview[]) {
  const headers = [
    'Date', 'Name', 'Source', 'Persona', 'Age group', 'Ethnicity', 'Channel', 'Last trip',
    'Pain', 'Wedge', 'Tools', 'Paid for?', 'Referral', 'Takeaway', 'Notes', 'Logged at',
  ];
  const data = rows.map(r => [
    r.interview_date, r.name, r.source, r.persona, r.age_group, r.ethnicity, r.channel, r.last_trip,
    r.pain, r.wedge, r.tools, r.paid, r.referral, r.takeaway, r.notes, r.created_at,
  ]);
  const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
  ws['!cols'] = [12, 16, 10, 10, 12, 18, 16, 26, 44, 12, 24, 26, 16, 26, 60, 20].map(wch => ({ wch }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Interviews');
  XLSX.writeFile(wb, `discovery-interviews-${today()}.xlsx`);
}

// ── Excel/CSV template + import ───────────────────────────────────────────────

const TEMPLATE_HEADERS = [
  'Date (YYYY-MM-DD)', 'Name', 'Source (stranger/network)', 'Persona (traveler/creator)',
  'Channel', 'Last trip', 'Pain (verbatim)', 'Wedge (companion/group/creator/none)',
  'Tools', 'Paid for? (what/why)', 'Referral', 'Takeaway',
];

const TEMPLATE_EXAMPLE = [
  '2026-07-20', 'Maya R.', 'stranger', 'traveler', 'UserInterviews',
  '5 days, Lisbon, with partner', 'We wasted an afternoon not knowing what was good nearby',
  'companion', 'Google Maps, saved IG posts', 'Paid $40 for a city guide PDF',
  'Priya S.', 'Companion pain is real + already paid',
];

/** The Week-1 questionnaire as a worksheet (reference tab). */
function questionnaireSheet() {
  const aoa: (string | number)[][] = [
    ['Interview questionnaire — Week 1 · The Mom Test'],
    [],
    ['Rules', QUESTIONNAIRE_RULES],
    [],
  ];
  for (const g of QUESTIONNAIRE) {
    aoa.push([g.title]);
    g.qs.forEach((q, i) => aoa.push([g.start + i, q]));
    aoa.push([]);
  }
  aoa.push(['Never ask', QUESTIONNAIRE_AVOID]);
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [{ wch: 10 }, { wch: 100 }];
  return ws;
}

/** Build + download the blank .xlsx template (also used as the in-app "Template" button). */
function downloadTemplate() {
  const ws = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS, TEMPLATE_EXAMPLE]);
  ws['!cols'] = TEMPLATE_HEADERS.map(() => ({ wch: 24 }));
  const guide = XLSX.utils.aoa_to_sheet([
    ['Field', 'Allowed values / notes'],
    ['Date', 'YYYY-MM-DD (or any date Excel recognises). Defaults to today if blank.'],
    ['Name', 'Required. A label is fine (e.g. "Maya R.").'],
    ['Source', 'stranger  |  network'],
    ['Persona', 'traveler  |  creator'],
    ['Wedge', 'companion  |  group  |  creator  |  none   (or leave blank)'],
    ['Everything else', 'Optional free text.'],
    ['Import', 'Delete the example row before uploading. Extra columns are ignored.'],
  ]);
  guide['!cols'] = [{ wch: 16 }, { wch: 66 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Interview Log');
  XLSX.utils.book_append_sheet(wb, questionnaireSheet(), 'Questionnaire');
  XLSX.utils.book_append_sheet(wb, guide, 'Guide');
  XLSX.writeFile(wb, 'interview-log-template.xlsx');
}

/**
 * A clean, user-facing version of the questionnaire — questions only.
 * Drops the internal Mom Test technique notes (Rules / Never ask) and the
 * strategy labels on section titles, so it's safe to share with respondents.
 */
function questionnaireSheetForSharing() {
  const aoa: (string | number)[][] = [
    ['Travel planning — a few questions about your trips'],
    [],
  ];
  for (const g of QUESTIONNAIRE) {
    aoa.push([g.title.split(' · ')[0]]); // keep the section name, drop the internal wedge label
    g.qs.forEach((q, i) => aoa.push([g.start + i, q]));
    aoa.push([]);
  }
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [{ wch: 10 }, { wch: 100 }];
  return ws;
}

/**
 * Export the questionnaire as a shareable kit: the questions on one sheet,
 * plus a blank sheet of the log columns to capture answers. No internal
 * technique notes. Log headers match the import parser, so a filled sheet
 * re-imports cleanly.
 */
function downloadQuestionnaireKit() {
  const logHeaders = [
    'Date (YYYY-MM-DD)', 'Name', 'Source (stranger/network)', 'Persona (traveler/creator)',
    'Age group', 'Ethnicity', 'Channel', 'Last trip', 'Pain (verbatim)',
    'Wedge (companion/group/creator/none)', 'Tools', 'Paid for? (what/why)',
    'Referral', 'Takeaway', 'Notes',
  ];
  const logWs = XLSX.utils.aoa_to_sheet([logHeaders]);
  logWs['!cols'] = logHeaders.map(() => ({ wch: 22 }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, questionnaireSheetForSharing(), 'Questionnaire');
  XLSX.utils.book_append_sheet(wb, logWs, 'Interview Log');
  XLSX.writeFile(wb, `travel-interview-questionnaire-${today()}.xlsx`);
}

// ── Wide export: one row per respondent, one column per survey question ───────
// Same order as the public survey (SurveyPage ALL_QS). The survey stores the
// full transcript in `notes` as "Q: …\nA: …" blocks in this order, so answers
// are read positionally — robust to punctuation drift.
const SURVEY_QUESTIONS: string[] = [
  'Tell us about the last trip you took — where, how long, who with?',
  'Walk us through one day of it — what did you actually do, roughly hour by hour?',
  'What did you plan ahead vs. figure out on the spot?',
  'Was there a moment you had unexpected free time or a gap in the day — what did you do?',
  'Ever stood somewhere unsure what to do or where to go next? Tell us about that time.',
  'When you wanted to find something good nearby in the moment, how did you decide?',
  'Anything you only found out later that you’d missed — and wished you’d known while you were there?',
  'Think about your last trip with other people — how did you all decide what to do each day?',
  'Where did that planning happen — group chat, a doc, in person? What got messy or lost?',
  'Someone usually becomes the “organiser.” Was that you? What did it cost you in time or stress?',
  'Ever followed someone’s trip online and thought “I want to do exactly that”? What did you do next?',
  'Have you ever tried to get someone’s itinerary — DM’d them, bought a guide, copied a blog? How did it go?',
  'Which apps/tools did you use during the trip? Which did you open most?',
  'What about the trip stressed you out or ate time you hadn’t expected?',
  'Have you ever paid for anything to help with a trip — a guide, app, planner, tour, agent? What, and why?',
  'If you paid for something: roughly how much did it cost — and was it worth it?',
  'Ever had a plan trapped in a PDF, email or doc you wished was just live in an app? What did you do with it?',
  'If you could wave a wand and fix one annoying thing about how you travel, what would it be?',
  'Who else do you know who travels like you that we should talk to? (a name or @handle is fine)',
];

/** Read the "Q: …\nA: …" transcript in `notes` into answers, in question order. */
function answersFromNotes(notes: string | null | undefined): string[] {
  if (!notes) return [];
  return notes.split(/\n\s*\n/).map(block => {
    const i = block.indexOf('\nA:');
    return i === -1 ? '' : block.slice(i + 3).trim();
  });
}

/** Export one row per respondent, one column per survey question (pain & paid kept as-is). */
function downloadWideResponses(rows: DiscoveryInterview[]) {
  const clean = (s: string) => (s && s !== '—' ? s : '');
  const headers = [
    'Date', 'Name', 'Persona', 'Source', 'Age group', 'Ethnicity',
    'Sharpest pain', 'Paid for?',
    ...SURVEY_QUESTIONS.map((q, i) => `Q${i + 1}. ${q}`),
  ];
  const data = rows.map(r => {
    const a = answersFromNotes(r.notes);
    return [
      r.interview_date, r.name, r.persona, r.source, r.age_group || '', r.ethnicity || '',
      r.pain || '', r.paid || '',
      ...SURVEY_QUESTIONS.map((_, i) => clean(a[i] || '')),
    ];
  });
  const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
  ws['!cols'] = headers.map((_, i) => ({ wch: i < 6 ? 12 : 40 }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Responses');
  XLSX.writeFile(wb, `discovery-responses-wide-${today()}.xlsx`);
}

const FIELD_BY_HEADER: Record<string, keyof CreateDiscoveryInterviewInput> = {
  date: 'interview_date', name: 'name', source: 'source', persona: 'persona',
  channel: 'channel', lasttrip: 'last_trip', pain: 'pain', wedge: 'wedge',
  tools: 'tools', paidfor: 'paid', paid: 'paid', referral: 'referral', takeaway: 'takeaway',
  notes: 'notes', agegroup: 'age_group', ethnicity: 'ethnicity',
};

// header -> canonical key: lowercase, drop parenthetical hints and punctuation
function normHeader(h: string): string {
  return h.toLowerCase().replace(/\(.*?\)/g, '').replace(/[^a-z0-9]/g, '');
}

function toDateStr(v: unknown): string {
  if (v instanceof Date && !isNaN(v.getTime())) {
    return `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, '0')}-${String(v.getDate()).padStart(2, '0')}`;
  }
  const s = String(v ?? '').trim();
  if (!s) return today();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (isNaN(d.getTime())) return today();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function normSource(v: unknown): InterviewSource {
  return String(v ?? '').toLowerCase().startsWith('net') ? 'network' : 'stranger';
}
function normPersona(v: unknown): InterviewPersona {
  return String(v ?? '').toLowerCase().startsWith('creat') ? 'creator' : 'traveler';
}
function normWedge(v: unknown): InterviewWedge | null {
  const s = String(v ?? '').toLowerCase().trim();
  return (['companion', 'group', 'creator', 'none'] as string[]).includes(s) ? (s as InterviewWedge) : null;
}

/** Parse an uploaded .xlsx/.csv into interview inputs. Rows without a name are skipped. */
async function parseFileToInputs(file: File): Promise<{ inputs: CreateDiscoveryInterviewInput[]; skipped: number }> {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array', cellDates: true });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { defval: '' });

  const inputs: CreateDiscoveryInterviewInput[] = [];
  let skipped = 0;
  const txt = (v: unknown) => { const s = String(v ?? '').trim(); return s ? s : null; };

  for (const row of raw) {
    // remap each cell to its canonical field
    const rec: Partial<Record<keyof CreateDiscoveryInterviewInput, unknown>> = {};
    for (const key of Object.keys(row)) {
      const field = FIELD_BY_HEADER[normHeader(key)];
      if (field) rec[field] = row[key];
    }
    const name = txt(rec.name);
    if (!name) { skipped++; continue; }
    inputs.push({
      interview_date: toDateStr(rec.interview_date),
      name,
      source: normSource(rec.source),
      persona: normPersona(rec.persona),
      channel: txt(rec.channel),
      last_trip: txt(rec.last_trip),
      pain: txt(rec.pain),
      wedge: normWedge(rec.wedge),
      tools: txt(rec.tools),
      paid: txt(rec.paid),
      referral: txt(rec.referral),
      takeaway: txt(rec.takeaway),
      notes: txt(rec.notes),
      age_group: txt(rec.age_group),
      ethnicity: txt(rec.ethnicity),
    });
  }
  return { inputs, skipped };
}

// ── Form ───────────────────────────────────────────────────────────────────

type FormState = {
  interview_date: string;
  name: string;
  source: InterviewSource;
  persona: InterviewPersona;
  channel: string;
  last_trip: string;
  pain: string;
  wedge: InterviewWedge | '';
  tools: string;
  paid: string;
  referral: string;
  takeaway: string;
  notes: string;
};

const emptyForm = (): FormState => ({
  interview_date: today(),
  name: '',
  source: 'stranger',
  persona: 'traveler',
  channel: '',
  last_trip: '',
  pain: '',
  wedge: '',
  tools: '',
  paid: '',
  referral: '',
  takeaway: '',
  notes: '',
});

// ═══════════════════════════════════════════════════════════════════════════════

export function DiscoveryInterviewsPage() {
  const [rows, setRows] = useState<DiscoveryInterview[]>([]);
  const [views, setViews] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [importing, setImporting] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // LLM analysis
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState('');
  const [showAnalysis, setShowAnalysis] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<DiscoveryInterview | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm());

  const [deleteTarget, setDeleteTarget] = useState<DiscoveryInterview | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setRows(await fetchDiscoveryInterviews());
      try { setViews(await fetchSurveyViewCount()); } catch { setViews(null); }
    } catch (err: any) {
      setError(err.message || 'Failed to load interviews');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setShowModal(true);
  };

  const openEdit = (r: DiscoveryInterview) => {
    setEditing(r);
    setForm({
      interview_date: r.interview_date,
      name: r.name,
      source: r.source,
      persona: r.persona,
      channel: r.channel || '',
      last_trip: r.last_trip || '',
      pain: r.pain || '',
      wedge: r.wedge || '',
      tools: r.tools || '',
      paid: r.paid || '',
      referral: r.referral || '',
      takeaway: r.takeaway || '',
      notes: r.notes || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.interview_date) return;
    setSaving(true);
    try {
      const clean = (s: string) => (s.trim() ? s.trim() : null);
      const payload: CreateDiscoveryInterviewInput = {
        interview_date: form.interview_date,
        name: form.name.trim(),
        source: form.source,
        persona: form.persona,
        channel: clean(form.channel),
        last_trip: clean(form.last_trip),
        pain: clean(form.pain),
        wedge: form.wedge || null,
        tools: clean(form.tools),
        paid: clean(form.paid),
        referral: clean(form.referral),
        takeaway: clean(form.takeaway),
        notes: clean(form.notes),
      };
      if (editing) {
        await updateDiscoveryInterview(editing.id, payload);
      } else {
        await createDiscoveryInterview(payload);
      }
      await load();
      setShowModal(false);
    } catch (err: any) {
      setError(err.message || 'Failed to save interview');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteDiscoveryInterview(deleteTarget.id);
      setRows(prev => prev.filter(r => r.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete interview');
    } finally {
      setDeleting(false);
    }
  };

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;
    setImporting(true);
    setError('');
    setNotice('');
    try {
      const { inputs, skipped } = await parseFileToInputs(file);
      if (inputs.length === 0) {
        setError(skipped > 0
          ? 'No rows imported — every row was missing a Name. Check the file has a header row.'
          : 'No rows found in that file.');
        return;
      }
      await createDiscoveryInterviews(inputs);
      await load();
      setNotice(`Imported ${inputs.length} interview${inputs.length === 1 ? '' : 's'}${skipped > 0 ? ` · skipped ${skipped} row${skipped === 1 ? '' : 's'} with no name` : ''}.`);
    } catch (err: any) {
      setError(err.message || 'Failed to import file');
    } finally {
      setImporting(false);
    }
  };

  const handleAnalyze = async () => {
    if (rows.length === 0) return;
    setAnalyzing(true);
    setError('');
    setShowAnalysis(true);
    setAnalysis('');
    try {
      const md = await analyzeDiscoveryInterviews(rows);
      setAnalysis(md || 'The analysis came back empty. Try again once you have a few more interviews.');
    } catch (err: any) {
      setShowAnalysis(false);
      setError(err.message || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  // Quick tally for the header
  const strangers = rows.filter(r => r.source === 'stranger').length;
  const surveySubs = rows.filter(r => (r.channel || '').toLowerCase() === 'survey').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-surface-900">Interview Log</h1>
          <p className="text-surface-500 mt-1">
            Customer-discovery interviews · {rows.length} logged
            {rows.length > 0 && <> · {strangers} strangers</>}
            {views != null && (
              <> · Survey: {views} view{views === 1 ? '' : 's'} → {surveySubs} response{surveySubs === 1 ? '' : 's'}
                {views > 0 && <> ({Math.round((surveySubs / views) * 100)}%)</>}
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleFile}
            className="hidden"
          />
          <button
            onClick={downloadTemplate}
            className="flex items-center gap-2 px-4 py-2 border border-surface-200 text-surface-700 rounded-xl text-sm font-medium hover:bg-surface-100 transition-colors"
          >
            <FileSpreadsheet size={16} /> Template
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            className="flex items-center gap-2 px-4 py-2 border border-surface-200 text-surface-700 rounded-xl text-sm font-medium hover:bg-surface-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Upload size={16} /> {importing ? 'Importing…' : 'Import Excel'}
          </button>
          <button
            onClick={() => downloadCsv(rows)}
            disabled={rows.length === 0}
            className="flex items-center gap-2 px-4 py-2 border border-surface-200 text-surface-700 rounded-xl text-sm font-medium hover:bg-surface-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Download size={16} /> Export CSV
          </button>
          <button
            onClick={() => downloadXlsx(rows)}
            disabled={rows.length === 0}
            className="flex items-center gap-2 px-4 py-2 border border-surface-200 text-surface-700 rounded-xl text-sm font-medium hover:bg-surface-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <FileSpreadsheet size={16} /> Export Excel
          </button>
          <button
            onClick={() => downloadWideResponses(rows)}
            disabled={rows.length === 0}
            title="One row per respondent, one column per survey question (pain & paid kept as-is)"
            className="flex items-center gap-2 px-4 py-2 border border-surface-200 text-surface-700 rounded-xl text-sm font-medium hover:bg-surface-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <FileSpreadsheet size={16} /> Export Q&amp;A
          </button>
          <button
            onClick={handleAnalyze}
            disabled={rows.length === 0 || analyzing}
            title={rows.length === 0 ? 'Log some interviews first' : 'Analyze the log with AI'}
            className="flex items-center gap-2 px-4 py-2 border border-brand-200 text-brand-700 bg-brand-50 dark:bg-brand-900/20 dark:text-brand-300 dark:border-brand-800/40 rounded-xl text-sm font-medium hover:bg-brand-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Sparkles size={16} /> {analyzing ? 'Analyzing…' : 'Analyze'}
          </button>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-brand-600 text-white rounded-xl text-sm font-medium hover:bg-brand-700 transition-colors"
          >
            <Plus size={16} /> Log interview
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="px-4 py-3 banner-red border rounded-xl text-sm">
          {error}
          <button onClick={() => setError('')} className="ml-2 font-medium underline">Dismiss</button>
        </div>
      )}

      {/* Success notice */}
      {notice && (
        <div className="px-4 py-3 rounded-xl text-sm border bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800/40">
          {notice}
          <button onClick={() => setNotice('')} className="ml-2 font-medium underline">Dismiss</button>
        </div>
      )}

      {/* Questionnaire (Week 1 · The Mom Test) */}
      <div className="glass-panel overflow-hidden">
        <div className="flex items-center">
          <button
            onClick={() => setShowGuide(v => !v)}
            className="flex-1 min-w-0 flex items-center justify-between px-4 py-3 text-left hover:bg-surface-50 transition-colors"
          >
            <span className="flex items-center gap-2 text-sm font-semibold text-surface-800">
              <ListChecks size={16} className="text-brand-600 shrink-0" />
              Interview questionnaire — Week 1 (The Mom Test)
              <span className="text-xs font-normal text-surface-400">· 18 questions</span>
            </span>
            {showGuide ? <ChevronDown size={16} className="text-surface-400" /> : <ChevronRight size={16} className="text-surface-400" />}
          </button>
          <button
            onClick={downloadQuestionnaireKit}
            title="Download the questions + a blank interview-log sheet (.xlsx)"
            className="mr-3 ml-1 shrink-0 flex items-center gap-1.5 px-3 py-1.5 border border-surface-200 text-surface-700 rounded-lg text-xs font-medium hover:bg-surface-100 transition-colors"
          >
            <Download size={14} /> Export
          </button>
        </div>
        {showGuide && (
          <div className="px-4 pb-5 pt-1 space-y-4 border-t border-surface-200">
            <div className="px-3 py-2 rounded-lg bg-emerald-50 text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300 text-xs leading-relaxed">
              <strong>Rules:</strong> {QUESTIONNAIRE_RULES}
            </div>
            {QUESTIONNAIRE.map(group => (
              <div key={group.title}>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-brand-600 mb-1.5">{group.title}</h4>
                <ol className="space-y-1">
                  {group.qs.map((q, i) => (
                    <li key={i} className="flex gap-2 text-sm text-surface-700">
                      <span className="text-surface-400 tabular-nums w-5 shrink-0">{group.start + i}.</span>
                      <span>{q}</span>
                    </li>
                  ))}
                </ol>
              </div>
            ))}
            <div className="px-3 py-2 rounded-lg text-xs leading-relaxed bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400">
              <strong>Never ask:</strong> {QUESTIONNAIRE_AVOID}
            </div>
          </div>
        )}
      </div>

      {/* Table / empty state */}
      {rows.length === 0 ? (
        <div className="text-center py-16">
          <ClipboardList className="w-12 h-12 text-surface-300 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-surface-700 mb-1">No interviews logged yet</h3>
          <p className="text-sm text-surface-500 mb-6">
            Log each discovery interview right after it ends — memory fades fast.
          </p>
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-600 text-white rounded-xl text-sm font-medium hover:bg-brand-700 transition-colors"
          >
            <Plus size={16} /> Log your first interview
          </button>
        </div>
      ) : (
        <div className="glass-panel overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-surface-200">
                <th className="table-header-cell w-28">Date</th>
                <th className="table-header-cell">Person</th>
                <th className="table-header-cell w-24">Source</th>
                <th className="table-header-cell w-20">Age</th>
                <th className="table-header-cell w-32">Ethnicity</th>
                <th className="table-header-cell w-28">Wedge</th>
                <th className="table-header-cell">Sharpest pain</th>
                <th className="table-header-cell w-40">Paid for?</th>
                <th className="table-header-cell w-24 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const src = sourceStyle(r.source);
                const wedge = wedgeStyle(r.wedge);
                return (
                  <tr key={r.id} className="table-row">
                    <td className="table-cell">
                      <span className="text-xs text-surface-600 whitespace-nowrap">{formatDate(r.interview_date)}</span>
                    </td>
                    <td className="table-cell">
                      <div className="text-sm font-medium text-surface-900 truncate max-w-[14rem]">{r.name}</div>
                      <div className="text-xs text-surface-500 capitalize">{r.persona}{r.channel ? ` · ${r.channel}` : ''}</div>
                    </td>
                    <td className="table-cell">
                      <span className={cn('text-xs px-2 py-0.5 rounded-md font-medium', src.cls)}>{src.label}</span>
                    </td>
                    <td className="table-cell">
                      <span className="text-xs text-surface-600 whitespace-nowrap">{r.age_group || <span className="text-surface-400">&mdash;</span>}</span>
                    </td>
                    <td className="table-cell">
                      <span className="text-xs text-surface-600">{r.ethnicity || <span className="text-surface-400">&mdash;</span>}</span>
                    </td>
                    <td className="table-cell">
                      {wedge
                        ? <span className={cn('text-xs px-2 py-0.5 rounded-md font-medium', wedge.cls)}>{wedge.label}</span>
                        : <span className="text-xs text-surface-400">&mdash;</span>}
                    </td>
                    <td className="table-cell">
                      <div className="text-sm text-surface-700 truncate max-w-xs">{r.pain || <span className="text-surface-400">&mdash;</span>}</div>
                    </td>
                    <td className="table-cell">
                      <div className="text-xs text-surface-600 truncate max-w-[10rem]">{r.paid || <span className="text-surface-400">&mdash;</span>}</div>
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEdit(r)}
                          className="p-2 rounded-lg hover:bg-surface-100 text-surface-500 hover:text-surface-700 transition-colors"
                          title="Edit"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(r)}
                          className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-surface-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Create / Edit Modal ────────────────────────────────────────────── */}
      {showModal && (
        <Modal
          open={showModal}
          title={editing ? 'Edit interview' : 'Log interview'}
          onClose={() => setShowModal(false)}
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-surface-700 mb-1">Date</label>
                <input
                  type="date"
                  value={form.interview_date}
                  onChange={e => setForm(f => ({ ...f, interview_date: e.target.value }))}
                  className="input-field text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-surface-700 mb-1">Name / label</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Maya R."
                  maxLength={120}
                  className="input-field text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-surface-700 mb-1">Source</label>
                <select
                  value={form.source}
                  onChange={e => setForm(f => ({ ...f, source: e.target.value as InterviewSource }))}
                  className="input-field text-sm"
                >
                  {SOURCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-surface-700 mb-1">Persona</label>
                <select
                  value={form.persona}
                  onChange={e => setForm(f => ({ ...f, persona: e.target.value as InterviewPersona }))}
                  className="input-field text-sm"
                >
                  {PERSONA_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-surface-700 mb-1">Wedge hit</label>
                <select
                  value={form.wedge}
                  onChange={e => setForm(f => ({ ...f, wedge: e.target.value as InterviewWedge | '' }))}
                  className="input-field text-sm"
                >
                  <option value="">—</option>
                  {WEDGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-surface-700 mb-1">Channel (how found)</label>
              <input
                type="text"
                value={form.channel}
                onChange={e => setForm(f => ({ ...f, channel: e.target.value }))}
                placeholder="UserInterviews · Reddit · referral · community…"
                className="input-field text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-surface-700 mb-1">Last trip</label>
              <input
                type="text"
                value={form.last_trip}
                onChange={e => setForm(f => ({ ...f, last_trip: e.target.value }))}
                placeholder="Where, how long, who with"
                className="input-field text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-surface-700 mb-1">Sharpest pain (verbatim)</label>
              <textarea
                value={form.pain}
                onChange={e => setForm(f => ({ ...f, pain: e.target.value }))}
                placeholder="Quote them in their own words"
                rows={3}
                className="input-field text-sm resize-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-surface-700 mb-1">Tools used</label>
                <input
                  type="text"
                  value={form.tools}
                  onChange={e => setForm(f => ({ ...f, tools: e.target.value }))}
                  placeholder="Maps, group chat, saved posts…"
                  className="input-field text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-surface-700 mb-1">Has paid for? (what / why)</label>
                <input
                  type="text"
                  value={form.paid}
                  onChange={e => setForm(f => ({ ...f, paid: e.target.value }))}
                  placeholder="A guide, app, tour, agent…"
                  className="input-field text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-surface-700 mb-1">Referral</label>
                <input
                  type="text"
                  value={form.referral}
                  onChange={e => setForm(f => ({ ...f, referral: e.target.value }))}
                  placeholder="Who else to talk to"
                  className="input-field text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-surface-700 mb-1">1-line takeaway</label>
                <input
                  type="text"
                  value={form.takeaway}
                  onChange={e => setForm(f => ({ ...f, takeaway: e.target.value }))}
                  placeholder="The one thing to remember"
                  className="input-field text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-surface-700 mb-1">Notes / full transcript</label>
              <textarea
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Raw notes or the full Q&A (survey submissions land here automatically)"
                rows={3}
                className="input-field text-sm resize-none"
              />
            </div>

            <div className="flex justify-end gap-3 pt-2 border-t border-surface-200">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm font-medium text-surface-600 hover:text-surface-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !form.name.trim() || !form.interview_date}
                className="px-5 py-2 bg-brand-600 text-white rounded-xl text-sm font-medium hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? 'Saving…' : editing ? 'Update' : 'Log it'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Delete Confirmation ────────────────────────────────────────────── */}
      {deleteTarget && (
        <Modal
          open={!!deleteTarget}
          title="Delete interview"
          onClose={() => setDeleteTarget(null)}
        >
          <div className="space-y-4">
            <p className="text-sm text-surface-600">
              Delete the interview with <strong>{deleteTarget.name}</strong>? This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-sm font-medium text-surface-600 hover:text-surface-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-5 py-2 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── AI analysis ────────────────────────────────────────────────────── */}
      {showAnalysis && (
        <Modal
          open={showAnalysis}
          title={`Analysis · ${rows.length} interview${rows.length === 1 ? '' : 's'}`}
          onClose={() => setShowAnalysis(false)}
        >
          <div className="space-y-4">
            {analyzing ? (
              <div className="flex flex-col items-center justify-center py-12 gap-3">
                <Spinner size="lg" />
                <p className="text-sm text-surface-500">Reading the log and finding the pattern…</p>
              </div>
            ) : (
              <>
                <div className="max-h-[60vh] overflow-y-auto pr-1">
                  <div className="prose prose-sm dark:prose-invert max-w-none prose-headings:text-brand-700 prose-headings:mt-4 prose-headings:mb-1 dark:prose-headings:text-brand-300">
                    <ReactMarkdown>{analysis}</ReactMarkdown>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-3 pt-2 border-t border-surface-200">
                  <p className="text-xs text-surface-400">AI-generated from your logged interviews · treat as a prompt for judgment, not a verdict.</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigator.clipboard?.writeText(analysis)}
                      className="flex items-center gap-1.5 px-3 py-1.5 border border-surface-200 text-surface-700 rounded-lg text-sm font-medium hover:bg-surface-100 transition-colors"
                    >
                      <Copy size={14} /> Copy
                    </button>
                    <button
                      onClick={handleAnalyze}
                      className="px-3 py-1.5 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 transition-colors"
                    >
                      Re-run
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
